/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 团队照片标签 hook
 *   返回当前团队的"可用标签列表"(已过滤 disabled), 供卡片悬浮 / 标签选择浮层使用。
 *   特点:
 *     - module-level 缓存 + 请求 dedup,同一个 groupID 下多张 Photo 卡片并发挂载只发 1 次请求
 *     - 5 分钟 TTL,过期自动重新拉
 *     - 暴露 `invalidate` 让打标/管理弹窗操作完毕主动失效缓存,下次取到最新数据
 */
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import { fetchTeamTags } from '../../pages/photoTag/api'
import type { PhotoTag } from '../../pages/photoTag/types'

interface CacheEntry {
  tags: PhotoTag[]
  expireAt: number
  pending?: Promise<PhotoTag[]>
}

const TTL_MS = 5 * 60 * 1000
const cache = new Map<string, CacheEntry>()
const subscribers = new Map<string, Set<(tags: PhotoTag[]) => void>>()

const cacheKey = (groupID: string, userID: string) => `${groupID}::${userID}`

function notify(key: string, tags: PhotoTag[]) {
  const subs = subscribers.get(key)
  if (!subs) return
  subs.forEach((fn) => fn(tags))
}

function ensureTags(groupID: string, userID: string): Promise<PhotoTag[]> {
  const key = cacheKey(groupID, userID)
  const entry = cache.get(key)
  const now = Date.now()
  if (entry && entry.expireAt > now && !entry.pending) {
    return Promise.resolve(entry.tags)
  }
  if (entry?.pending) {
    return entry.pending
  }
  const pending = fetchTeamTags(groupID, userID)
    .then((tags) => {
      // 过滤 disabled — Web 端 fetchTeamTags 走的是 manage 接口 (返回全量含 disabled),
      // 卡片悬浮选择场景跟打标场景一致, 不能给用户看到停用标签
      const enabled = (tags || []).filter((t) => !t.disabled)
      cache.set(key, {
        tags: enabled,
        expireAt: Date.now() + TTL_MS
      })
      notify(key, enabled)
      return enabled
    })
    .catch((err) => {
      // 拉失败先清掉 pending,下次重试;不缓存空数组以免覆盖好数据
      const cur = cache.get(key)
      if (cur) cache.set(key, { ...cur, pending: undefined })
      else cache.delete(key)
      throw err
    })

  cache.set(key, {
    tags: entry?.tags || [],
    expireAt: entry?.expireAt || 0,
    pending
  })
  return pending
}

/**
 * 主动失效缓存 + **立即重拉** + 通知已挂载的订阅者。
 * 打标成功 / 移除标签 / 标签管理弹窗保存后调用, 所有挂载中的 hook 会同步收到新数据。
 *
 * 之前只 `cache.delete`, 但已挂载组件持有 useState 里的 stale tags,
 * 不会自己 refetch — 表现为「popover 里"最近常用"还是旧数据」「新增标签看不见」「删除后旧的还在」.
 * 这里改成: 清缓存 → 调 ensureTags 重拉 → 在 ensureTags 内 notify(key, fresh).
 *
 * 注: 不传 groupID = 清全部 (只在测试 / 切换团队场景, 不主动重拉, 等下次 hook mount).
 */
export function invalidateTeamPhotoTagsCache(
  groupID?: string,
  userID?: string
): void {
  if (!groupID) {
    cache.clear()
    return
  }
  if (!userID) {
    // 收集这个 groupID 下所有 user 的 entry, 清完再逐个重拉
    const keysToRefetch: Array<{ gid: string; uid: string }> = []
    Array.from(cache.keys()).forEach((k) => {
      if (k.startsWith(`${groupID}::`)) {
        const uid = k.slice(`${groupID}::`.length)
        cache.delete(k)
        keysToRefetch.push({ gid: groupID, uid })
      }
    })
    keysToRefetch.forEach(({ gid, uid }) => {
      // 触发重拉, ensureTags 成功后会自动 notify(key, fresh) 通知订阅者
      ensureTags(gid, uid) // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {})
    })
    return
  }
  cache.delete(cacheKey(groupID, userID))
  ensureTags(groupID, userID) // eslint-disable-next-line @typescript-eslint/no-empty-function
    .catch(() => {})
}

/**
 * 返回当前团队的可用标签(已过滤 disabled)。
 * 同一团队下多个组件并发挂载会复用同一份 Promise + 缓存,只发 1 次请求。
 *
 * @param options.enabled 默认 true. 传 false 时跳过 fetch (返回 [])
 *   —— 用于 Photo 共享组件在非打标场景 (选照片器 / 加水印 / 分类文件夹) 避免无谓请求。
 *   非打标场景的 Photo 已经被 tagOverlayEnabled gate 不渲染 UI, 这里把 hook 也短路掉,
 *   消除剩余的网络开销。
 */
export function useTeamPhotoTags(options?: { enabled?: boolean }): {
  tags: PhotoTag[]
  loading: boolean
  refresh: () => void
} {
  const enabled = options?.enabled !== false
  const groupID = useSelector(
    (state: RootState) => ((state.group as any)?.groupId as string) || ''
  ) as string
  const userID = useSelector(
    (state: RootState) => ((state.main as any)?.user?.user_id as string) || ''
  )

  const [tags, setTags] = useState<PhotoTag[]>(() => {
    if (!enabled || !groupID || !userID) return []
    const entry = cache.get(cacheKey(groupID, userID))
    return entry?.tags || []
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return
    if (!groupID || !userID) return
    const key = cacheKey(groupID, userID)

    // 订阅: 其他组件主动 invalidate 后,这里能立刻拿到新数据
    let subs = subscribers.get(key)
    if (!subs) {
      subs = new Set()
      subscribers.set(key, subs)
    }
    const handler = (next: PhotoTag[]) => setTags(next)
    subs.add(handler)

    let cancelled = false
    const entry = cache.get(key)
    const fresh = entry && entry.expireAt > Date.now() && !entry.pending
    if (!fresh) {
      setLoading(true)
      ensureTags(groupID, userID)
        .then((next) => {
          if (!cancelled) {
            setTags(next)
            setLoading(false)
          }
        })
        .catch(() => {
          if (!cancelled) setLoading(false)
        })
    } else if (entry) {
      setTags(entry.tags)
    }

    return () => {
      cancelled = true
      subs!.delete(handler)
      if (subs!.size === 0) subscribers.delete(key)
    }
  }, [enabled, groupID, userID])

  return {
    tags,
    loading,
    refresh: () => {
      if (!enabled || !groupID || !userID) return
      invalidateTeamPhotoTagsCache(groupID, userID)
      ensureTags(groupID, userID) // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {})
    }
  }
}
